import { merge } from "lodash"
import { colors, createTheme, responsiveFontSizes } from "@material-ui/core"
import { esES as coreEs } from "@material-ui/core/locale"
import { esES } from "@mui/x-data-grid"

import Colors from "src/components/Common/Colors"
import { THEMES } from "../constants"
import { softShadows, strongShadows } from "./shadows"
import typography from "./typography"

const baseOptions = {
  direction: "ltr",
  typography,
  overrides: {
    MuiPickersModal: {
      dialogRoot: {
        "&&": {
          width: 310,
          minWidth: 0,
          maxWidth: "calc(100% - 32px)",
          margin: 16,
        },
      },
      dialogRootWider: {
        "&&": { width: 325 },
      },
      dialog: {
        "&&": { padding: 0 },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 12,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 12,
        backgroundColor: "#f5f7fb",
        boxShadow: "0 18px 60px rgba(13,35,71,.24)",
      },
      paperWidthXs: { maxWidth: 960 },
      paperWidthSm: { maxWidth: 960 },
      paperWidthMd: { maxWidth: 1100 },
      paperFullWidth: {
        width: "calc(100% - 48px)",
        margin: 24,
        "@media (max-width: 599px)": { width: "calc(100% - 24px)", margin: 12 },
      },
      paperFullScreen: {
        borderRadius: 0,
        width: "100%",
        maxWidth: "100%",
        margin: 0,
      },
    },
    MuiDialogContent: {
      root: {
        padding: "20px 24px",
        backgroundColor: "#f5f7fb",
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#fff",
          borderRadius: 7,
        },
      },
      dividers: { borderColor: "#dce4ef" },
    },
    MuiDialogActions: {
      root: {
        padding: "16px 24px",
        backgroundColor: "#f5f7fb",
        borderTop: "1px solid #dce4ef",
        flexWrap: "wrap",
        gap: 8,
        "& .MuiButton-root": {
          minHeight: 38,
          borderRadius: 8,
          margin: 0,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { transform: "none" },
        },
      },
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: Colors.DARK_BLUE,
        backgroundImage: "linear-gradient(110deg, #0d2347, #244f7e)",
        color: "white",
        padding: "20px 24px",
        "& h2": { fontSize: 18, fontWeight: 600 },
        "& .MuiIconButton-root": { color: "white" },
      },
    },
    MuiCardHeader: {
      root: {
        backgroundColor: Colors.DARK_BLUE,
        color: "white",
      },
    },
    MuiTableSortLabel: {
      root: {
        "&:hover": {
          color: colors.common.white,
        },
        "&$active": {
          color: colors.common.white,
        },
      },
      icon: {
        color: "inherit !important",
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 3,
        overflow: "hidden",
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32,
      },
    },
    MuiChip: {
      root: {
        backgroundColor: "rgba(0,0,0,0.075)",
      },
    },
    MuiTable: {
      root: {
        minWidth: 700,
      },
    },
    MuiTextField: {
      root: {
        transition: "box-shadow 0.3s ease-in-out",
        "&:hover": {
          boxShadow: "0px 4px 10px rgba(0, 123, 255, 0.4)",
        },
      },
    },
    MuiAvatar: {
      img: {
        width: 25,
        height: 25,
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: "rgba(31, 55, 77, 0.45)",
        color: "#fff",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.45,
        padding: "10px 14px",
        borderRadius: 12,
        maxWidth: 280,
        textAlign: "left",
        border: "1px solid rgba(255, 255, 255, 0.22)",
        boxShadow:
          "0 12px 32px rgba(15, 23, 42, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
        backdropFilter: "blur(16px) saturate(160%)",
        WebkitBackdropFilter: "blur(16px) saturate(160%)",
      },
      arrow: {
        color: "rgba(31, 55, 77, 0.45)",
      },
    },
  },
  palette: {
    common: {
      black: "#000",
      white: "#fff",
    },
    error: {
      light: "#e57373",
      main: "#f44336",
      dark: "#d32f2f",
      contrastText: "#fff",
    },
    warning: {
      light: "#ffb74d",
      main: "#ff9800",
      dark: "#f57c00",
      contrastText: "rgba(0, 0, 0, 0.87)",
    },
    info: {
      light: "#64b5f6",
      main: "#2196f3",
      dark: "#1976d2",
      contrastText: "#fff",
    },
    success: {
      light: "#81c784",
      main: "#4caf50",
      dark: "#388e3c",
      contrastText: "#081208",
    },
    divider: "rgba(0, 0, 0, 0.4)",
  },
  props: {
    MuiDialog: { fullWidth: true, maxWidth: "md" },
    // Name of the component ⚛️
    MuiButton: {
      // The default props to change
      size: "small",
      color: "primary",
    },
    MuiTable: {
      size: "small",
    },
    MuiIconButton: {
      color: "primary",
    },
    MuiIcon: {
      color: "primary",
    },
    MuiTextField: {
      variant: "outlined",
      size: "small",
    },
  },
}

const themesOptions = [
  {
    name: THEMES.LIGHT,
    overrides: {
      MuiInputBase: {
        input: {
          "&::placeholder": {
            opacity: 1,
            color: colors.blueGrey[600],
          },
        },
      },
    },
    palette: {
      type: "light",
      action: {
        active: Colors.DARK_BLUE,
        disabledBackground: "rgba(255, 255, 255, 0.4)",
      },
      background: {
        default: colors.common.white,
        paper: colors.common.white,
      },
      primary: {
        main: Colors.DARK_BLUE,
        contrastText: "#fff",
      },
      secondary: {
        main: Colors.LIGTH_BLUE,
        contrastText: "#fff",
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[600],
      },
    },
    shadows: softShadows,
  },

  {
    name: THEMES.ONE_DARK,
    palette: {
      type: "dark",
      action: {
        active: "rgba(255, 255, 255, 0.54)",
        hover: "rgba(255, 255, 255, 0.04)",
        selected: "rgba(255, 255, 255, 0.08)",
        disabled: "rgba(255, 255, 255, 0.26)",
        disabledBackground: "rgba(255, 255, 255, 0.5)",
        focus: "rgba(255, 255, 255, 0.12)",
      },
      background: {
        default: "#282C34",
        dark: "#1c2025",
        paper: "#282C34",
      },
      primary: {
        main: "#8a85ff",
      },
      secondary: {
        main: "#8a85ff",
      },
      text: {
        primary: "#e6e5e8",
        secondary: "#adb0bb",
        white: "#ffffff",
      },
    },
    shadows: strongShadows,
  },
  {
    name: THEMES.UNICORN,
    palette: {
      type: "dark",
      action: {
        active: "rgba(255, 255, 255, 0.54)",
        hover: "rgba(255, 255, 255, 0.04)",
        selected: "rgba(255, 255, 255, 0.08)",
        disabled: "rgba(255, 255, 255, 0.26)",
        disabledBackground: "rgba(255, 255, 255, 0.12)",
        focus: "rgba(255, 255, 255, 0.12)",
      },
      background: {
        default: "#2a2d3d",
        dark: "#222431",
        paper: "#2a2d3d",
      },
      primary: {
        main: "#a67dff",
      },
      secondary: {
        main: "#a67dff",
      },
      text: {
        primary: "#f6f5f8",
        secondary: "#9699a4",
      },
    },
    shadows: strongShadows,
  },
]

export const customTheme = (config = {}) => {
  let themeOptions = themesOptions.find(theme => theme.name === config.name)

  if (!themeOptions) {
    ;[themeOptions] = themesOptions
  }

  let theme = createTheme(
    merge(baseOptions, themeOptions, config.custom),
    esES,
    coreEs
  )

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme)
  }

  return theme
}
